import dayjs from 'dayjs'
import type { RequestOptions, SearchOptions } from '@nuxtjs/algolia'
import type { SearchFilterOptions } from '~/composables/useSearchFilters'
import type { CartItem } from '~/composables/types/cart'
import type { EventDescription } from '~/composables/types/event'
import { localitySlugForAlgoliaOptionalFilter } from '~/utils/localitySlugMatch'

const PRICE_MULTIPLIER = 100
const getISO2Locale = (locale: string): string => {
  const ISO2LocaleParts = locale.split('-')
  return ISO2LocaleParts[0]
}

const getEventListIndexName = (locale: string = 'de-de'): string => {
  return `events_${getISO2Locale(locale)}`
}

const getTopEventListIndexName = (locale: string): string => {
  return `events_${getISO2Locale(locale)}_top_sold_events`
}

const getEventListSortByDateAscIndexName = (locale: string): string => {
  return `events_${getISO2Locale(locale)}_sort_by_created_at_asc`
}

const getEventListSortByDateDescIndexName = (locale: string): string => {
  return `events_${getISO2Locale(locale)}_sort_by_created_at_desc`
}

const getEventListSortByPriceAscIndexName = (locale: string): string => {
  return `events_${getISO2Locale(locale)}_sort_by_price_asc`
}

const getAutocompleteQuerySuggestionsIndexName = (locale: string): string => {
  return `events_${getISO2Locale(locale)}_query_suggestions`
}

const getTopGiftIdeasIndexName = (locale: string): string => {
  return `events_${getISO2Locale(locale)}_top_gift_ideas`
}

const getCitiesIndexName = (): string => {
  return 'locations'
}

const getLocationsIndexName = (): string => {
  return 'locations'
}

const getQuerySuggestionsIndex = (locale: string): string => {
  return `events_${getISO2Locale(locale)}_query_suggestions`
}

const getCategoriesIndexName = (locale: string): string => {
  return `categories_${getISO2Locale(locale)}`
}

const getBoxesIndexName = (locale: string): string => {
  return `products_${getISO2Locale(locale)}`
}

/**
 * Kits in Algolia use `products_*`. KFT often sets `type: NATIONWIDE` without
 * `specialization: KIT`; use this for conversion/insights index routing only.
 */
const getAlgoliaMerchandisingIndexForEventDescription = (
  event: EventDescription | undefined,
  locale: string,
  /** When the record is not a kit (ignored if the event is a kit). */
  nonKitIndexOverride?: string
): string => {
  const isKit = event?.specialization === 'KIT' || event?.type === 'NATIONWIDE'
  if (isKit) {
    return getBoxesIndexName(locale)
  }
  return nonKitIndexOverride || getEventListIndexName(locale)
}

const cartItemIsKitForAlgoliaMerchandising = (item: CartItem): boolean => {
  if (item.specialization === 'KIT') return true
  if (item.attendanceMode?.toUpperCase() === 'NATIONWIDE') return true
  const ev = item.event
  return ev?.specialization === 'KIT' || ev?.type === 'NATIONWIDE'
}

const getAlgoliaMerchandisingIndexForCartItem = (
  item: CartItem,
  locale: string
): string =>
  cartItemIsKitForAlgoliaMerchandising(item)
    ? getBoxesIndexName(locale)
    : getEventListIndexName(locale)

/** Stable key for checkout session maps (cart id vs order id may differ in type). */
const normalizeAlgoliaCheckoutMapKey = (
  id: string | number | undefined | null
): string | null => {
  if (id === undefined || id === null || id === '') return null
  return String(id)
}

/**
 * Pick Algolia merchandising index for a completed order line.
 * Prefer order payload (`type` / `specialization`) so mixed carts split correctly;
 * fall back to session map from checkout when description lacks those fields.
 */
const resolveAlgoliaMerchandisingIndexForPurchaseLine = (
  description: { type?: string; specialization?: string } | undefined | null,
  sessionIndexName: string | undefined,
  locale: string
): string => {
  if (
    description?.specialization === 'KIT' ||
    description?.type === 'NATIONWIDE'
  ) {
    return getBoxesIndexName(locale)
  }
  if (description?.type != null || description?.specialization != null) {
    return getEventListIndexName(locale)
  }
  if (sessionIndexName) {
    return sessionIndexName
  }
  return getEventListIndexName(locale)
}

export interface OrderItemLikeForAlgoliaPurchase {
  quantity: number
  price?: { amount: string }
  characteristics?: {
    description?: {
      id?: string | number | null
      type?: string
      specialization?: string
    }
  }
}

export interface AlgoliaPurchaseItemInput {
  eventDescriptionId: string
  queryID?: string
  price?: number
  quantity: number
  indexName: string
}

/**
 * Maps order API lines + checkout session maps to Algolia purchase payloads.
 * Keeps order-success and unit tests aligned on index/query resolution.
 */
const buildAlgoliaPurchaseItemsFromOrderLines = (
  orderLines: OrderItemLikeForAlgoliaPurchase[] | undefined | null,
  queryIdMap: Record<string, string>,
  indexNameMap: Record<string, string>,
  locale: string
): AlgoliaPurchaseItemInput[] => {
  if (!orderLines?.length) return []
  const purchaseItems: AlgoliaPurchaseItemInput[] = []
  for (const item of orderLines) {
    const description = item.characteristics?.description
    const eventDescriptionId = normalizeAlgoliaCheckoutMapKey(description?.id)
    if (!eventDescriptionId) continue
    const price = item.price?.amount
      ? parseInt(item.price.amount, 10) / 100
      : undefined
    const sessionIndex = indexNameMap[eventDescriptionId]
    const indexName = resolveAlgoliaMerchandisingIndexForPurchaseLine(
      description,
      sessionIndex,
      locale
    )
    purchaseItems.push({
      eventDescriptionId,
      queryID: queryIdMap[eventDescriptionId],
      price,
      quantity: item.quantity,
      indexName,
    })
  }
  return purchaseItems
}

/** Persist Algolia query/index maps before cart clear (order-success purchase insights). */
const persistCheckoutAlgoliaMapsToSessionStorage = (
  items: CartItem[] | undefined,
  locale: string
): void => {
  if (typeof sessionStorage === 'undefined') return
  const queryIdMap: Record<string, string> = {}
  const indexNameMap: Record<string, string> = {}
  if (items) {
    items.forEach((item) => {
      const key = normalizeAlgoliaCheckoutMapKey(item.eventDescriptionId)
      if (!key) return
      if (item.queryID) {
        queryIdMap[key] = item.queryID
      }
      indexNameMap[key] = getAlgoliaMerchandisingIndexForCartItem(item, locale)
    })
  }
  if (Object.keys(queryIdMap).length > 0) {
    sessionStorage.setItem('checkout-query-ids', JSON.stringify(queryIdMap))
  }
  if (Object.keys(indexNameMap).length > 0) {
    sessionStorage.setItem('checkout-index-names', JSON.stringify(indexNameMap))
  }
}

const getFederatedSearchIndexName = (locale: string): string => {
  return `events_${getISO2Locale(locale)}`
}

const fnGetFacetFilters = (array: string[], facetName: string) =>
  array.map((f) => `${facetName}:${f}`)

// const getCategoryFilters = (categories: string[], categorySlugs: string[]) => {
//   if (categorySlugs && categorySlugs.length) {
//     return categorySlugs.map((cslug) => `categories.slug:${cslug}`)
//   }
//   if (categories?.length > 0) {
//     const categoriesArr = categories?.map(
//       (category) => `categories.name:${category}`
//     )
//     return categoriesArr
//     // return  `[${categoriesArr.join(',')}]`
//   }
//
//   return []
// }

const parseFiltersToAlgoliaParams = (filters: Partial<SearchFilterOptions>) => {
  const result: string[] = []

  if (Number(filters.tags?.length) > 0) {
    result.push(`(tags.name: "${filters.tags?.[0]}")`)
  }

  if (filters.location) {
    const slug = localitySlugForAlgoliaOptionalFilter(filters.location)
    const useRegions =
      filters.locationType === 'region' || filters.location.includes('--')
    result.push(`${useRegions ? 'regions' : 'cities'}:${slug}`)
  }

  const price = [
    filters.priceMin || filters.priceMin === 0
      ? `default_price.amount >= ${filters.priceMin * PRICE_MULTIPLIER}`
      : null,
    filters.priceMax
      ? `default_price.amount <= ${filters.priceMax * PRICE_MULTIPLIER}`
      : null,
  ]
    .filter((item) => {
      return typeof item !== 'undefined' && item !== '' && item !== null
    })
    .join(' AND ')
  if (price) result.push(price)
  if (filters.dates?.length) {
    const dates_ts = (filters.dates || []).map((d) => dayjs(d).unix())
    // If it's the same date, add one day to second date
    // Or if there's only one date, add one day to it
    if (dates_ts[0] === dates_ts[1] || (dates_ts[0] && !dates_ts[1])) {
      dates_ts[1] = dates_ts[0] + 24 * 60 * 60
    }
    result.push(
      [
        `dates_ts:${dates_ts.join(' TO ')}`,
        `dates_private_ts:${dates_ts.join(' TO ')}`,
      ].join(' OR ')
    )
  }

  if (filters.hasAvailableDates) {
    result.push('has_events:true')
  }

  if (filters.hasPromotion) {
    result.push('has_promotions:true')
  }

  if (filters.typeOfClass) {
    if (filters.typeOfClass === 'directEvents') {
      result.push('has_direct_events:true')
    }

    if (filters.typeOfClass === 'publicEvents') {
      result.push('has_public_events:true')
    }

    /**
     * Show online and offline events together only when
     * we don't have `location` filter in URL
     * when `location` and `online` selected => show only online events
     */
    if (filters.location === undefined && filters.typeOfClass !== 'online') {
      result.push('is_online:true OR is_online:false')
    } else {
      result.push(
        filters.typeOfClass === 'online' ? 'is_online:true' : 'is_online:false'
      )
    }
  }

  return result.map((f) => `(${f})`).join(' AND ')
}

const getAlgoliaSearchOptions = ({
  filters,
  rawOptions,
}: {
  filters: SearchFilterOptions
  rawOptions: Partial<SearchOptions & RequestOptions>
}): SearchOptions => {
  return {
    query: filters?.searchquery,
    facetFilters: [
      fnGetFacetFilters(
        filters.categories?.map((c) => c?.toString() || '') || [],
        'categories.name'
      ),
      fnGetFacetFilters([], 'categories.slug'),
      fnGetFacetFilters(filters.languages || [], 'languages'),
    ].filter((filters) => filters.length),
    filters: parseFiltersToAlgoliaParams({
      ...filters,
    }),
    ...rawOptions,
  }
}

type FederatedSearchAnalyticsPlacement = 'homepage' | 'navbar'

/** Federated search landing outcome (stored in session until the list page loads). */
type FederatedSearchAnalyticsOutcome =
  | 'search_results'
  | 'category_results'
  | 'location_results'

const FEDERATED_SEARCH_ANALYTICS_OUTCOMES: FederatedSearchAnalyticsOutcome[] = [
  'search_results',
  'category_results',
  'location_results',
]

const isFederatedSearchAnalyticsOutcome = (
  value: string
): value is FederatedSearchAnalyticsOutcome =>
  FEDERATED_SEARCH_ANALYTICS_OUTCOMES.includes(
    value as FederatedSearchAnalyticsOutcome
  )

/** Appends `appEnvironment` to every Algolia request; dedupes if already present. */
const buildAlgoliaAnalyticsTags = (
  appEnvironment: string,
  additionalTags?: readonly string[] | null
): string[] => {
  const withoutEnv = (additionalTags ?? []).filter(
    (tag) => tag !== appEnvironment
  )
  return [...withoutEnv, appEnvironment]
}

const getFederatedSearchAnalyticsTags = (
  appEnvironment: string,
  placement?: FederatedSearchAnalyticsPlacement
) =>
  buildAlgoliaAnalyticsTags(
    appEnvironment,
    placement ? [placement, 'as-you-type'] : ['as-you-type']
  )

/** Federated list-page outcome only (env merged via `buildAlgoliaAnalyticsTags`). */
const getListPageFederatedAnalyticsTags = (
  federatedOutcome?: string | null
): readonly string[] => {
  const outcome = federatedOutcome?.toString()
  if (!outcome || !isFederatedSearchAnalyticsOutcome(outcome)) {
    return []
  }

  return [outcome]
}

export {
  getISO2Locale,
  getTopEventListIndexName,
  getEventListIndexName,
  getEventListSortByDateAscIndexName,
  getEventListSortByDateDescIndexName,
  getEventListSortByPriceAscIndexName,
  getAutocompleteQuerySuggestionsIndexName,
  getCitiesIndexName,
  getLocationsIndexName,
  getTopGiftIdeasIndexName,
  getQuerySuggestionsIndex,
  getCategoriesIndexName,
  getBoxesIndexName,
  getFederatedSearchIndexName,
  FEDERATED_SEARCH_ANALYTICS_OUTCOMES,
  type FederatedSearchAnalyticsPlacement,
  type FederatedSearchAnalyticsOutcome,
  buildAlgoliaAnalyticsTags,
  getFederatedSearchAnalyticsTags,
  getListPageFederatedAnalyticsTags,
  getAlgoliaSearchOptions,
  getAlgoliaMerchandisingIndexForEventDescription,
  getAlgoliaMerchandisingIndexForCartItem,
  normalizeAlgoliaCheckoutMapKey,
  persistCheckoutAlgoliaMapsToSessionStorage,
  resolveAlgoliaMerchandisingIndexForPurchaseLine,
  buildAlgoliaPurchaseItemsFromOrderLines,
}
